import { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import type {
  QuoteItemDTO,
  QuoteProductDTO
} from '@setvi/shared/services/react-query';
import { updateQuoteV2ItemPricingMutation } from 'Services/Mutations/QuotesV2';
import { AutoRFQQueryKey } from 'Services';
import type { QuoteV2ItemWithTotalsResponse } from 'Services/Query/QuotesV2/Types';
import {
  updateItemOptimistically,
  findItemById,
  composeQuoteDTO,
  updateItemFromServerResponse
} from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

interface ItemUpdates {
  sell?: number;
  totalGrossProfit?: number;
  totalGrossProfitPercentage?: number;
}

interface UpdateItemParams {
  itemId: string;
  updates: ItemUpdates;
}

interface PendingUpdate {
  itemId: string;
  fields: ItemUpdates;
}

const PRICING_FIELDS = [
  'sell',
  'totalGrossProfit',
  'totalGrossProfitPercentage'
] as const;

/** Check if updates contain pricing-related fields */
const hasPricingFields = (updates: ItemUpdates): boolean =>
  PRICING_FIELDS.some(f => updates[f] !== undefined);

/**
 * Debounced item update hook that accumulates changes
 *
 * Key behavior:
 * 1. Applies optimistic updates IMMEDIATELY to items cache (instant UI feedback)
 * 2. Debounces only the API save operation
 * 3. Captures pre-change state before first edit for proper rollback
 * 4. Routes pricing fields to V2 pricing endpoint
 */
export const useDebouncedItemUpdate = (quoteId: string, delay = 1500) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn: updatePricing } = updateQuoteV2ItemPricingMutation();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousItemsRef = useRef<QuoteItemDTO[] | undefined>(undefined);
  const pendingUpdateRef = useRef<PendingUpdate | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const lockedRef = useRef(false);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const lock = useCallback(() => {
    cancel();
    pendingUpdateRef.current = null;
    previousItemsRef.current = undefined;
    lockedRef.current = true;
  }, [cancel]);

  const unlock = useCallback(() => {
    lockedRef.current = false;
  }, []);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const mutate = useCallback(
    ({ itemId, updates }: UpdateItemParams) => {
      // Skip if locked (e.g. refresh in progress — prevents stale debounced edits from overwriting)
      if (lockedRef.current) return;

      // Capture pre-change state on FIRST change only (before any optimistic updates)
      if (!timeoutRef.current) {
        previousItemsRef.current = cache.getItems();
      }

      // Accumulate pending updates
      if (pendingUpdateRef.current?.itemId === itemId) {
        pendingUpdateRef.current.fields = {
          ...pendingUpdateRef.current.fields,
          ...updates
        };
      } else {
        pendingUpdateRef.current = { itemId, fields: { ...updates } };
      }

      // Apply optimistic update IMMEDIATELY to items cache
      const currentItems = cache.getItems();
      const composed = composeQuoteDTO(cache.getQuote(), currentItems);
      const updated = updateItemOptimistically(composed, itemId, updates);
      if (updated) {
        cache.setItems(() => updated.items);
      }

      // Cancel existing debounce timer
      cancel();

      // Set new debounce timer for API call
      timeoutRef.current = setTimeout(async () => {
        timeoutRef.current = null;
        setIsLoading(true);
        setError(null);

        const pending = pendingUpdateRef.current;
        pendingUpdateRef.current = null;

        if (!pending) {
          setIsLoading(false);
          return;
        }

        try {
          await cache.cancel();

          if (hasPricingFields(pending.fields)) {
            const items = cache.getItems() || [];
            const item = findItemById(items, pending.itemId);

            if (item) {
              const product = item as QuoteProductDTO;
              const sell = pending.fields.sell ?? product.sell ?? 0;
              const cost = product.cost ?? 0;
              const gpAmount =
                pending.fields.totalGrossProfit ??
                product.totalGrossProfit ??
                sell - cost;
              const gpPercent =
                pending.fields.totalGrossProfitPercentage ??
                product.totalGrossProfitPercentage ??
                0;

              const data = await updatePricing({
                quoteId,
                itemId: pending.itemId,
                sell: Number(sell),
                cost: Number(cost),
                gpPercent: Number(gpPercent),
                gpAmount: Number(gpAmount)
              });

              const response = data as QuoteV2ItemWithTotalsResponse;

              if (response?.item) {
                cache.setItems(old =>
                  updateItemFromServerResponse(old || [], response.item, p => ({
                    quantity: p.quantity,
                    sell: p.sell,
                    cost: p.cost,
                    totalGrossProfit: p.marginAmount,
                    totalGrossProfitPercentage: p.margin
                  }))
                );
              }

              if (response?.totals) {
                cache.setQuote(old =>
                  old
                    ? {
                        ...old,
                        totalSell: response.totals.totalSell,
                        totalCost: response.totals.totalCost
                      }
                    : old
                );
              }
            }
          }

          cache.invalidate();

          // RFQ-linked quotes embed the Product Match grid, which renders from
          // the AutoRFQ GET_ITEMS pages — a cache the V2 invalidate() above
          // never touches. A price saved here is synced server-side back onto
          // the RFQ item, so invalidate that query (scoped to this rfq) to
          // refetch the new price when the user opens the Product Match tab,
          // instead of leaving the stale cached page until a full reload. Only
          // for pricing edits (quantity doesn't change the RFQ item card), and
          // only when RFQ-linked. Default refetchType: the grid is unmounted
          // while editing on the Quote tab, so it refetches on re-open.
          if (hasPricingFields(pending.fields)) {
            const rfqId = cache.getQuote()?.rfqId;
            if (rfqId) {
              cache.queryClient.invalidateQueries({
                queryKey: [AutoRFQQueryKey.GET_ITEMS, rfqId]
              });
            }
          }
        } catch (err) {
          // Rollback ALL accumulated changes on error
          if (previousItemsRef.current) {
            cache.setItems(() => previousItemsRef.current);
          }
          setError(err instanceof Error ? err : new Error('Update failed'));
        } finally {
          setIsLoading(false);
          previousItemsRef.current = undefined;
        }
      }, delay);
    },
    [cache, cancel, delay, quoteId, updatePricing]
  );

  return useMemo(
    () => ({
      mutate,
      cancel,
      lock,
      unlock,
      isLoading,
      error
    }),
    [mutate, cancel, lock, unlock, isLoading, error]
  );
};
